import { List } from '@common/typescript/objects/List';
import { WithDeleted } from '@common/typescript/objects/WithDeleted';
import { Lang } from '@common/typescript/objects/Lang';
import { Nullable } from '@common/typescript/objects/Nullable';
import { IPublished } from '@common/typescript/objects/IPublished';
import { IAvatarTarget } from '@common/typescript/objects/IAvatarTarget';
import { Page as BasePage, PageInfo as BasePageInfo, WithLangs } from '@common/typescript/objects/Page';

import { PageImage } from '@commonTuna/react/objects/PageImage';
import { LangsInfo } from '@commonTuna/react/objects/LangsInfo';

import { CasePage } from '@app/objects/CasePage';
import { FaqPage } from '@app/objects/FaqPage';
import { ReviewPage } from '@app/objects/ReviewPage';
import { capitalizeFirstLetter } from '@app/components/Utils';
import { Review } from '@app/objects/Review';
import { PageDoctor } from '@app/objects/PageDoctor';
import { IAutoTranslation } from '@app/objects/IAutoTranslation';

export interface PagePage {
	parent: Page;
	id: number;
	deleted: boolean;
	pageId: number;
	parentId: number;
}

export enum PageType {
	PAGE = 0,
	SERVICE = 1
}

export const PageTypeNames = {
	[PageType.PAGE]: 'General',
	[PageType.SERVICE]: 'Service',
};

export enum Anesthesia
{
	None = 0,
	General = 1,
	Sedation = 2,
	Regional = 3,
	Local = 4
}

export const AnesthesiaNames = {
	[Anesthesia.None]: 'None',
	[Anesthesia.General]: 'General',
	[Anesthesia.Sedation]: 'Sedation',
	[Anesthesia.Regional]: 'Regional',
	[Anesthesia.Local]: 'Local',
};

export enum FactsBlock
{
	None = 0,
	Left = 1,
	Right = 2
}

export const FactsBlockNames = {
	[FactsBlock.None]: 'None',
	[FactsBlock.Left]: 'Left',
	[FactsBlock.Right]: 'Right',
};

export enum PageUpdateFrequency {
	Always = 0,
	Hourly = 1,
	Daily = 2,
	Weekly = 3,
	Monthly = 4,
	Yearly = 5,
	Never = 6
}

export const PageUpdateFrequencyNames = {
	[PageUpdateFrequency.Always]: 'Always',
	[PageUpdateFrequency.Hourly]: 'Hourly',
	[PageUpdateFrequency.Daily]: 'Daily',
	[PageUpdateFrequency.Weekly]: 'Weekly',
	[PageUpdateFrequency.Monthly]: 'Monthly',
	[PageUpdateFrequency.Yearly]: 'Yearly',
	[PageUpdateFrequency.Never]: 'Never',
};

export interface PageInfo extends BasePageInfo {
	shortBody: string;
	author: string;
}

export interface PageVideo extends WithDeleted, LangsInfo {
	page: Page;
	pageId: number;
	url: string;
}

export const rootPageId = 3;

export interface Page extends WithDeleted, IPublished, BasePage, IAvatarTarget, IAutoTranslation {
	duration: string;
	shortNameEn: string;
	shortNameEs: string;
	tagLineEn: string;
	tagLineEs: string;
	basePath?: string;
	show: boolean;
	menu: boolean;
	allowStyles: boolean;
	hasWrongLink: boolean;
	text: string;
	children: List<Page>;
	pages: List<PagePage>;
	fullPath: string;
	pageType: PageType;
	files: Array<PageImage>;
	videos: Array<PageVideo>;
	surgeryLength: string;
	recoveryTime: string;
	purposeEn: string;
	purposeEs: string;
	anesthesia: Anesthesia;
	factsBlock: FactsBlock;
	cases: Array<CasePage>;
	doctors: Array<PageDoctor>;
	faqs: Array<FaqPage>;
	reviews: Array<ReviewPage>;
	randomReviews: Array<Review>;
	parentPage: Nullable<Page>;
	pathEs: string;
	fullPathEs: string;
	langs: Array<PageInfo>;
	fromPages: Array<PageCombineWith>;
	sortOrder: number;
	updateFrequency: PageUpdateFrequency;
	updateFrequencyEs: PageUpdateFrequency;
	priority: number;
	priorityEs: number;
	default: boolean;
	parentIsRootPage: boolean;
}

export interface PageCombineWith extends WithDeleted {
	fromPage: Page;
	fromId: number;

	toPage: Page;
	toId: number;
}

export const getPageInfo = (item: WithLangs<PageInfo>, lang: Lang): PageInfo | undefined => {
	return item.langs.length > 0 ? (item.langs.find((langItem) => langItem.language === lang) || item.langs[0]) : undefined;
};

export const getPageShortName = (item: Page, lang: Lang): string => {
	return lang === Lang.Es
		? item.shortNameEs || item.shortNameEn || item.name
		: item.shortNameEn || item.shortNameEs || item.name;
};

export const getLangByName = (language: string): Lang => {
	return (Lang[capitalizeFirstLetter(language.toLowerCase())] || Lang.En) as Lang;
};
